const TOKEN_KEY = "token"
const USER_KEY = "user"
const SESSIONS_KEY = "authSessions"
const AUTH_SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000
const MAX_STORED_SESSIONS = 20

const hasBrowserStorage = () => typeof window !== "undefined" && window.localStorage && window.sessionStorage

const parseJson = (value, fallback = null) => {
  if (!value) return fallback
  try {
    return JSON.parse(value)
  } catch {
    return fallback
  }
}

const writeStorage = (storage, key, value) => {
  try {
    storage.setItem(key, value)
  } catch (error) {
    console.warn(`[authStorage] Unable to write ${key}`, error)
  }
}

const removeStorage = (storage, key) => {
  try {
    storage.removeItem(key)
  } catch (error) {
    console.warn(`[authStorage] Unable to remove ${key}`, error)
  }
}

export const getAuthToken = () => {
  if (!hasBrowserStorage()) return null
  const token = window.sessionStorage.getItem(TOKEN_KEY) || window.localStorage.getItem(TOKEN_KEY)
  if (!token || token === "undefined" || token === "null" || token === "false" || token.trim() === "") return null
  return token
}

export const getAuthUser = () => {
  if (!hasBrowserStorage()) return null
  return parseJson(window.sessionStorage.getItem(USER_KEY)) || parseJson(window.localStorage.getItem(USER_KEY))
}

export const getStoredAuthSessions = () => {
  if (!hasBrowserStorage()) return []

  const now = Date.now()
  const sessions = parseJson(window.localStorage.getItem(SESSIONS_KEY), [])
  if (!Array.isArray(sessions)) return []

  return sessions.filter((session) => {
    if (!session?.token) return false
    if (!session.expiresAt) return true
    return new Date(session.expiresAt).getTime() > now
  })
}

export const persistAuthSession = (token, user) => {
  if (!hasBrowserStorage() || !token || !user) return

  const normalizedUser = {
    ...user,
    id: user.id || user._id,
    _id: user._id || user.id,
  }
  const userJson = JSON.stringify(normalizedUser)
  const now = Date.now()
  const expiresAt = new Date(now + AUTH_SESSION_TTL_MS).toISOString()
  const userId = normalizedUser._id || normalizedUser.id || normalizedUser.email || "user"
  const sessions = getStoredAuthSessions().filter((session) => session.token !== token)

  sessions.push({
    sessionId: `${userId}-${now}`,
    token,
    user: normalizedUser,
    userId,
    email: normalizedUser.email || "",
    createdAt: new Date(now).toISOString(),
    lastUsedAt: new Date(now).toISOString(),
    expiresAt,
  })

  const nextSessions = sessions.slice(-MAX_STORED_SESSIONS)

  writeStorage(window.sessionStorage, TOKEN_KEY, token)
  writeStorage(window.sessionStorage, USER_KEY, userJson)

  // Keep the legacy keys updated for old code paths and refreshed tabs.
  writeStorage(window.localStorage, TOKEN_KEY, token)
  writeStorage(window.localStorage, USER_KEY, userJson)
  writeStorage(window.localStorage, SESSIONS_KEY, JSON.stringify(nextSessions))
}

export const updateStoredAuthUser = (user) => {
  const token = getAuthToken()
  if (token && user) {
    persistAuthSession(token, user)
  }
}

export const clearActiveAuthSession = () => {
  if (!hasBrowserStorage()) return

  removeStorage(window.sessionStorage, TOKEN_KEY)
  removeStorage(window.sessionStorage, USER_KEY)

  // Only clear the active legacy keys. Saved sessions remain available.
  removeStorage(window.localStorage, TOKEN_KEY)
  removeStorage(window.localStorage, USER_KEY)
}
